const CancellationAndRefundPolicy = () => {
  return (
    <div className="Policies">
      <div className="container max-w-6xl mx-auto px-4">
        <h1>APOLO P2P</h1>
        <h2>Refund, Cancellation and Rescheduling Policy</h2>

        <p>
          At APOLO, our goal is to create a fair and reliable tutoring marketplace for both students
          and tutors. Because APOLO operates as a platform that connects students with independent
          tutors, this policy explains how cancellations, rescheduling, refunds and disputes are
          handled.
        </p>
        <p>By booking a session on APOLO, users agree to the terms below.</p>
        <ol>
          <li style={{ fontSize: '24px' }}>
            <h3>Nature of the Service and Payment Responsibility</h3>
            <p>
              APOLO is an online marketplace that facilitates booking and payment for one-to-one
              tutoring sessions delivered by independent tutors.
            </p>
            <ul>
              <li>Tutors are responsible for delivering the tutoring service.</li>
              <li>
                Payments are processed through third-party payment providers and are paid directly
                to tutors.
              </li>
              <li>APOLO does not hold user funds and does not act as the service provider.</li>
              <li>APOLO may mediate disputes and enforce platform policies when necessary.</li>
            </ul>
            <p>
              Refunds are issued by tutors through the payment provider, subject to this policy.
            </p>
            <p>
              Platform service fees charged to tutors are non-refundable once a session has been
              booked or delivered.
            </p>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Student Cancellation Policy</h3>
            <p>
              Students may cancel a scheduled session without penalty up to{' '}
              <b>48 hours before the scheduled start time.</b>
            </p>
            <p>If a cancellation occurs less than 48 hours before the session:</p>
            <ul>
              <li>The session is considered delivered</li>
              <li>The session fee is non-refundable</li>
              <li>The tutor will be compensated for the reserved time</li>
            </ul>
            <p>
              Failure to attend a scheduled session without cancellation (“no-show”) is treated as a
              completed session.
            </p>
            <p>
              Students arriving more than <b>15 minutes late</b> may be considered absent at the
              tutor’s discretion.
            </p>
            <p>To prevent abuse, student cancellations are limited to:</p>
            <ul>
              <li>Maximum 30 percent of booked sessions per week </li>
              <li>Maximum of 5 cancellations per week</li>
            </ul>
            <p>
              APOLO reserves the right to suspend accounts that demonstrate abusive cancellation
              behavior.
            </p>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Session Rescheduling Policy</h3>
            <p>
              Rescheduling is permitted up to <b>24 hours before the session start time,</b> subject
              to tutor availability.
            </p>
            <p>Rescheduling requires:</p>
            <ul>
              <li>Tutor approval of the new time slot</li>
              <li>Completion of the rescheduling through the platform</li>
            </ul>
            <p>
              Late rescheduling requests (less than 24 hours) may be declined by the tutor and
              treated as a late cancellation.
            </p>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Tutor Cancellation Policy</h3>
            <p>
              Tutors are required to provide at least <b>24 hours notice</b> when cancelling a
              session.
            </p>
            <p>If a tutor cancels a session:</p>
            <ul>
              <li>The student may reschedule the session, or</li>
              <li>The student may request a full refund of the session fee</li>
            </ul>
            <p>
              Repeated late cancellations by tutors may lead to account penalties or removal from
              the platform.
            </p>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Technical Issues and Interrupted Sessions</h3>
            <p>Because sessions take place online, technical issues may occasionally occur.</p>
            <p>If a session is significantly affected by technical problems such as:</p>
            <ul>
              <li>Internet connection failures</li>
              <li>Platform outages</li>
              <li>Audio or video malfunction</li>
            </ul>
            <p>Students may request:</p>
            <ul>
              <li>A rescheduled session, or</li>
              <li>A partial or full refund depending on session completion</li>
            </ul>
            <p>
              Technical claims must be submitted within <b>48 hours</b> of the session.
            </p>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Dispute Resolution</h3>
            <p>APOLO provides a dispute mediation process to support fair outcomes.</p>
            <p>
              A dispute may be opened within <b>72 hours after the session</b> if a user believes:
            </p>
            <ul>
              <li>The session did not take place</li>
              <li>The session ended prematurely due to tutor fault</li>
              <li>The tutor failed to appear</li>
              <li>There was serious misconduct or violation of platform rules</li>
            </ul>
            <p>Users may be asked to provide supporting evidence, including:</p>
            <ul>
              <li>Session recordings</li>
              <li>Chat logs</li>
              <li>Platform activity data</li>
            </ul>
            <p>
              APOLO will review the evidence and issue a decision. Platform decisions are final.
            </p>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Withdrawal and Refund Requests</h3>
            <p>
              Students may close their account and request a refund of their remaining wallet
              balance at any time.
            </p>
            <p>Refunds will exclude:</p>
            <ul>
              <li>Fees for completed sessions</li>
              <li>Fees for late cancellations or no-shows</li>
              <li>Non-refundable processing or administrative fees</li>
              <li>Promotional or bonus credits</li>
            </ul>
            <p>
              Approved refunds are processed within <b>10–15 business days</b> via the original
              payment method, subject to payment provider timelines.
            </p>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Refund Ineligibility</h3>
            <p>Refunds will not be issued for:</p>

            <ul>
              <li>Sessions canceled after the 48-hour cancellation window</li>
              <li> Sessions already completed or partially delivered</li>
              <li>Subjective dissatisfaction with teaching style or learning outcomes</li>
              <li>Accounts suspended for violation of Terms of Service or Code of Conduct</li>
              <li>Inactive accounts requesting refunds after six months of inactivity</li>
            </ul>
            <p>APOLO does not guarantee learning outcomes.</p>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Digital Service Acknowledgment</h3>
            <p>By booking a session, users acknowledge that:</p>
            <p>If a tutor cancels a session:</p>
            <ul>
              <li>The tutoring service begins at the scheduled session time</li>
              <li>The right of withdrawal no longer applies once the session has taken place</li>
            </ul>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Anti-Abuse and Platform Protection</h3>
            <p>APOLO reserves the right to suspend or terminate accounts that demonstrate:</p>
            <ul>
              <li>Excessive cancellations or refund requests</li>
              <li>Abuse of tutors or students</li>
              <li>Attempts to bypass the platform</li>
              <li>Fraudulent payment or chargeback activity</li>
            </ul>
          </li>

          <li style={{ fontSize: '24px' }}>
            <h3>Changes to This Policy</h3>
            <p>
              This policy may be updated periodically to reflect operational or legal requirements.
              Continued use of the platform constitutes acceptance of any updates.
            </p>
            <p>
              If you have any questions or concerns about our policy, please contact us at{' '}
              <a href="mailto:students@apolop2p.com">students@apolop2p.com</a>.
            </p>
          </li>
        </ol>
      </div>
    </div>
  )
}

export default CancellationAndRefundPolicy
